import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import Footer from "@/components/Footer";
import { useBranding } from "@/hooks/useBranding";

export default function ForgotPassword() {
  const branding = useBranding();
  const [step, setStep] = useState("email");
  const [credential, setCredential] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const isEmail = (v) => v.includes("@");
  const requestReset = async (e) => {
    e?.preventDefault?.();
    if (!credential.trim()) { toast.error("Enter your email or mobile number"); return; }
    setBusy(true);
    try {
      const payload = isEmail(credential) ? { email: credential.trim() } : { mobile_number: credential.trim() };
      await api.post("/auth/forgot-password", payload);
      setToken("");
      toast.success("Reset code sent to your email");
      setStep("reset");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (token.trim().length !== 6) { toast.error("Enter the 6-digit reset code"); return; }
    if (newPassword.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      const payload = isEmail(credential) ? { email: credential.trim(), token, new_password: newPassword } : { mobile_number: credential.trim(), token, new_password: newPassword };
      await api.post("/auth/reset-password", payload);
      toast.success("Password reset successfully. You can now log in.");
      setStep("done");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-background">
      <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 font-display font-bold text-2xl text-foreground tracking-tight">
            <img src={branding.logo_url || "/favicon.png"} alt={branding.product_name} className="h-8 w-8 rounded-lg object-cover" />
            {branding.product_name}
          </Link>
          <p className="text-muted-foreground mt-2 text-sm">Reset your password</p>
        </div>
        <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
          {step === "email" && (
            <form onSubmit={requestReset} className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground">Email or mobile number</label>
                <input
                  type="text"
                  value={credential}
                  onChange={(e) => setCredential(e.target.value)}
                  placeholder="you@example.com or +919876543210"
                  className="mt-1.5 h-11 w-full rounded-xl border border-border bg-background px-4 text-sm outline-none focus:border-primary transition-colors"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-full h-12 bg-primary text-primary-foreground hover:bg-[#1a3025] font-semibold disabled:opacity-50"
              >
                {busy ? "Sending…" : "Send reset code"}
              </button>
              <p className="text-xs text-muted-foreground text-center">
                Remember your password? <Link to="/login" className="text-primary underline">Log in</Link>
              </p>
            </form>
          )}
          {step === "reset" && (
            <form onSubmit={submitReset} className="space-y-4">
              <div className="rounded-2xl bg-secondary p-4 text-sm text-foreground">
                We sent a 6-digit code to the email on your account.
                <p className="text-xs text-muted-foreground mt-1">It expires in 10 minutes.</p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Reset code</label>
                <InputOTP
                  maxLength={6}
                  value={token}
                  onChange={setToken}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  containerClassName="mt-2 justify-between"
                >
                  <InputOTPGroup className="gap-2">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <InputOTPSlot
                        key={index}
                        index={index}
                        className="h-12 w-11 rounded-xl border border-border bg-background text-base font-semibold shadow-none first:rounded-xl first:border-l last:rounded-xl"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">New password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 8 characters"
                  className="mt-1.5 h-11 w-full rounded-xl border border-border bg-background px-4 text-sm outline-none focus:border-primary transition-colors"
                  minLength={8}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-full h-12 bg-primary text-primary-foreground hover:bg-[#1a3025] font-semibold disabled:opacity-50"
              >
                {busy ? "Resetting…" : "Reset password"}
              </button>
              <button
                type="button"
                onClick={requestReset}
                disabled={busy}
                className="w-full text-sm text-primary underline underline-offset-4 disabled:opacity-50"
              >
                Send a new code
              </button>
            </form>
          )}
          {step === "done" && (
            <div className="text-center space-y-4">
              <div className="text-4xl">✓</div>
              <p className="text-foreground font-semibold">Password reset complete</p>
              <Link
                to="/login"
                className="inline-block rounded-full h-12 px-6 bg-primary text-primary-foreground hover:bg-[#1a3025] font-semibold leading-[3rem]"
              >
                Log in
              </Link>
            </div>
          )}
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
}
